import { Form, Input } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'
import api from '@utils/api'

import { UploadingImages } from '@components/Admin/Product/ManageLidCodes/UploadingImages'
import { GraviButton, Horizontal, Texto } from '@gravitate-js/excalibrr'
import { extractColors } from 'extract-colors'
import { useQueryClient } from '@tanstack/react-query'
import { useTankLidCodes } from '@api/useTankLidCodes'

export const LidCodeForm = ({ lidCodeItem, handleClose }) => {
  const [mainColor, setMainColor] = useState('')
  const [selectedImage, setSelectedImage] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [form] = Form.useForm()
  const { useLidCodeUpsertMutation } = useTankLidCodes()
  const queryClient = useQueryClient()
  const handleReset = () => {
    form.resetFields()
    setSelectedImage(null)
    setMainColor('')
    setHasError(false)
    setIsUploading(false)
    handleClose()
  }
  const saveNewItem = async (values) => {
    if (!values.name) return
    if (!selectedImage && !lidCodeItem) {
      setHasError(true)
      return
    }
    try {
      setIsUploading(true)
      let formData = null
      if (selectedImage) {
        formData = new FormData()
        formData.append('image', selectedImage, selectedImage?.name)
      }

      const payload = {
        name: values.name,
        color: mainColor,
        image: formData ? formData : null,
        tank_lid_id: lidCodeItem?._id,
      }

      await api.upsertTankLidCode(payload)
      await queryClient.invalidateQueries({
        queryKey: ['lidCodeOverview'],
      })
      handleReset()
    } catch (e) {
      console.error({ e })
    } finally {
      setIsUploading(false)
    }
  }
  const updateDominantColor = (imageSrc) => {
    extractColors(imageSrc).then((colors) => {
      const sortedColors = colors.sort((a, b) => b.area - a.area)
      const dominantColor = sortedColors[0]
      setMainColor(dominantColor.hex)
    })
  }
  const src = useMemo(() => {
    if (!selectedImage && lidCodeItem && lidCodeItem.image_url) return lidCodeItem.image_url
    if (!selectedImage) return null
    const imageSrc = URL.createObjectURL(selectedImage)
    updateDominantColor(imageSrc)
    return imageSrc
  }, [selectedImage])

  const handleFileChange = async (info) => {
    setSelectedImage(info)
    setHasError(false)
  }
  useEffect(() => {
    if (lidCodeItem) {
      form.setFieldsValue({
        name: lidCodeItem.name,
        color: lidCodeItem.color_hex,
        image: lidCodeItem.image_url,
      })
      setMainColor(lidCodeItem.color_hex)
    }
  }, [lidCodeItem])

  return (
    <Form form={form} onFinish={saveNewItem} layout={'vertical'}>
      <Form.Item
        name={'name'}
        label={'Product Name'}
        rules={[
          {
            required: true,
            message: 'Please enter product name',
          },
        ]}
      >
        <Input placeholder={'Enter Product Name'} style={{ width: '250px' }} />
      </Form.Item>
      <Form.Item name={'image'} label={'Lid Icon'}>
        <UploadingImages handleFileChange={handleFileChange} src={src} hasError={hasError} />
      </Form.Item>

      {src && (
        <Form.Item name={'color'} label={'Display Color'} className={'m-0'}>
          <label htmlFor={'custom-color-input'}>
            <Horizontal verticalCenter className={'bordered py-1 px-2'} style={{ width: '100px' }}>
              <div className={'bordered'} style={{ backgroundColor: mainColor, height: '12px', width: '12px' }}></div>
              <Texto className={'ml-3'} textTransform={'uppercase'}>
                {mainColor}
              </Texto>
            </Horizontal>
          </label>
          <input
            accept={'image/png, image/jpeg, image/jpg image/svg+xml'}
            id={'custom-color-input'}
            type={'color'}
            value={mainColor}
            onChange={(e) => {
              setMainColor(e.target.value)
            }}
          />
        </Form.Item>
      )}

      <Horizontal className={'mt-4 pb-4 pt-4 border-bottom border-top'} verticalCenter>
        <GraviButton
          disabled={isUploading}
          style={{ borderRadius: '5px' }}
          buttonText={'Cancel'}
          onClick={handleClose}
        />
        <GraviButton
          className={'ml-4'}
          theme1
          style={{ borderRadius: '5px' }}
          buttonText={'Save'}
          htmlType={'submit'}
          loading={isUploading}
        />
      </Horizontal>
    </Form>
  )
}
